import React, { useState } from 'react';
import avatarEmma from '../assets/avatars/emma.svg';

const mockCandidate = {
  fullName: 'Jane Doe',
  headline: 'Senior Backend Engineer (Fintech)',
  location: 'San Francisco, CA',
  email: 'jane.doe@example.com',
  linkedinUrl: 'https://www.linkedin.com/in/janedoe',
  totalExperienceYears: 8,
  domain: 'Fintech',
  teamSize: 'Led teams of 4–6',
  storyHighlights: [
    'Scaled payment APIs handling millions of transactions/month.',
    'Led a team of backend engineers across 3 major projects.',
    'Deep experience with Python, distributed systems, and AWS.'
  ],
  education: [
    {
      id: 'edu-1',
      institution: 'MIT',
      degree: 'B.Sc. Computer Science',
      fieldOfStudy: 'Distributed Systems',
      startDate: '2012',
      endDate: '2016',
      gpa: '3.8'
    }
  ],
  experiences: [
    {
      id: 'exp-1',
      companyName: 'Stripe',
      companyLogoUrl: 'https://logo.clearbit.com/stripe.com',
      title: 'Senior Backend Engineer',
      location: 'San Francisco, CA',
      startDate: 'Mar 2021',
      endDate: 'Present',
      duration: '3 yrs',
      bullets: [
        'Led design and implementation of high-throughput payment APIs.',
        'Improved p95 latency by 35% through service decomposition and caching.',
        'Mentored 4 engineers and drove technical roadmap for core payments.'
      ],
      skillTags: ['Python', 'Distributed Systems', 'PostgreSQL', 'AWS', 'Fintech']
    },
    {
      id: 'exp-2',
      companyName: 'ExxonMobil',
      companyLogoUrl: 'https://logo.clearbit.com/exxonmobil.com',
      title: 'Senior Process Engineer',
      location: 'Houston, TX',
      startDate: 'Jan 2018',
      endDate: 'Feb 2021',
      duration: '3 yrs 2 mos',
      bullets: [
        'Optimized downstream processes, reducing energy usage by 12%.',
        'Introduced data-driven monitoring with Python-based dashboards.',
        'Collaborated with cross-functional teams across multiple plants.'
      ],
      skillTags: ['Process Optimization', 'Python', 'Data Analysis', 'Oil & Gas']
    }
  ],
  skills: {
    core: ['Python', 'Django', 'PostgreSQL', 'AWS', 'Docker'],
    domain: ['Fintech', 'Payments', 'Risk'],
    leadership: ['Team Leadership', 'Stakeholder Management', 'Technical Roadmapping']
  },
  fitSummary: {
    matchScore: 87,
    strengths: [
      '5+ years experience in Fintech and payments.',
      'Proven track record leading backend teams.',
      'Strong distributed systems and API design background.'
    ],
    concerns: ['Limited recent front-end experience.']
  }
};

function ExperienceTimelineItem({ experience, isActive, onSelect }) {
  const initials = experience.companyName
    .split(' ')
    .map((word) => word[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();

  return (
    <div
      className={`cs-timeline-item ${isActive ? 'cs-timeline-item-active' : ''}`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <div className="cs-timeline-dot" />
      <div className="cs-timeline-content">
        <div className="cs-timeline-header">
          <div className="cs-company-logo-wrapper">
            {experience.companyLogoUrl ? (
              <img
                src={experience.companyLogoUrl}
                alt={experience.companyName}
                className="cs-company-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="cs-company-logo-fallback">{initials}</div>
            )}
          </div>
          <div>
            <div className="cs-timeline-title">
              {experience.title}
            </div>
            <div className="cs-timeline-company">{experience.companyName}</div>
          </div>
        </div>
        <div className="cs-timeline-meta">
          <span>
            {experience.startDate} – {experience.endDate}
          </span>
          <span className="cs-dot">•</span>
          <span>{experience.duration}</span>
          {experience.location && (
            <>
              <span className="cs-dot">•</span>
              <span>{experience.location}</span>
            </>
          )}
        </div>
        <div className="cs-timeline-description">
          {experience.bullets[0]}
        </div>
      </div>
    </div>
  );
}

export default function CandidateStoryPage() {
  const c = mockCandidate;
  const slides = ['Overview', 'Experience', 'Skills'];
  const [currentIndex, setCurrentIndex] = useState(1); // start on Experience
  const [selectedExperience, setSelectedExperience] = useState(c.experiences[0]);

  const goPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goNext = () => {
    setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const currentSlide = slides[currentIndex];

  return (
    <div className="cs-page cs-page-centered">
      <div className="cs-story-card">
        <div className="cs-card-header">
          <div className="cs-card-header-left">
            <div className="cs-avatar-large">
              <img src={avatarEmma} alt={c.fullName} className="cs-avatar-img" />
            </div>
            <div>
              <div className="cs-card-title">{c.fullName}</div>
              <div className="cs-card-subtitle">{c.headline}</div>
            </div>
          </div>
          <div className="cs-slide-nav">
            <button className="cs-slide-btn" onClick={goPrev} aria-label="Previous section">
              ‹
            </button>
            <div className="cs-slide-dots">
              {slides.map((label, index) => (
                <button
                  key={label}
                  className={`cs-slide-dot ${index === currentIndex ? 'cs-slide-dot-active' : ''}`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={label}
                />
              ))}
            </div>
            <button className="cs-slide-btn" onClick={goNext} aria-label="Next section">
              ›
            </button>
          </div>
        </div>

        <div className="cs-summary-row">
          <div className="cs-summary-item">
            <div className="cs-summary-label">Current role</div>
            <div className="cs-summary-value">{c.headline}</div>
          </div>
          <div className="cs-summary-item">
            <div className="cs-summary-label">Experience</div>
            <div className="cs-summary-value">{c.totalExperienceYears}+ years</div>
          </div>
          <div className="cs-summary-item">
            <div className="cs-summary-label">Location</div>
            <div className="cs-summary-value">{c.location}</div>
          </div>
        </div>

        <div className="cs-card-body">
          {currentSlide === 'Overview' && (
            <section className="cs-slide">
              <h2 className="cs-slide-title">Overview</h2>
              <p className="cs-slide-text">
                Senior backend engineer in {c.domain}, {c.totalExperienceYears}+ years of experience.
              </p>
              <ul className="cs-bullets">
                {c.storyHighlights.map((h, idx) => (
                  <li key={idx}>{h}</li>
                ))}
              </ul>
              <div className="cs-contact-inline">
                <a href={`mailto:${c.email}`} className="cs-link">
                  Email
                </a>
                <span className="cs-dot">•</span>
                <a href={c.linkedinUrl} target="_blank" rel="noreferrer" className="cs-link">
                  LinkedIn
                </a>
              </div>
            </section>
          )}

          {currentSlide === 'Experience' && (
            <section className="cs-slide">
              <h2 className="cs-slide-title">Experience timeline</h2>
              <div className="cs-timeline-container">
                <div className="cs-timeline-line" />
                <div className="cs-timeline-list">
                  {c.experiences.map((exp) => (
                    <ExperienceTimelineItem
                      key={exp.id}
                      experience={exp}
                      isActive={selectedExperience?.id === exp.id}
                      onSelect={() => setSelectedExperience(exp)}
                    />
                  ))}
                </div>
              </div>

              {selectedExperience && (
                <div className="cs-experience-detail">
                  <div className="cs-experience-detail-title">
                    {selectedExperience.title} at {selectedExperience.companyName}
                  </div>
                  <div className="cs-experience-detail-meta">
                    <span>
                      {selectedExperience.startDate} – {selectedExperience.endDate}
                    </span>
                    <span className="cs-dot">•</span>
                    <span>{selectedExperience.duration}</span>
                    {selectedExperience.location && (
                      <>
                        <span className="cs-dot">•</span>
                        <span>{selectedExperience.location}</span>
                      </>
                    )}
                  </div>
                  <ul className="cs-bullets cs-experience-detail-bullets">
                    {selectedExperience.bullets.map((b, idx) => (
                      <li key={idx}>{b}</li>
                    ))}
                  </ul>
                  <div className="cs-skill-tags cs-experience-detail-tags">
                    {selectedExperience.skillTags.map((tag) => (
                      <span key={tag} className="cs-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {currentSlide === 'Skills' && (
            <section className="cs-slide">
              <h2 className="cs-slide-title">Skills over the timeline</h2>

              <div className="cs-skills-group">
                <div className="cs-skills-label">Core technical</div>
                <div className="cs-skill-tags">
                  {c.skills.core.map((s) => (
                    <span key={s} className="cs-tag">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div className="cs-skills-group">
                <div className="cs-skills-label">Domain</div>
                <div className="cs-skill-tags">
                  {c.skills.domain.map((s) => (
                    <span key={s} className="cs-tag">
                      {s}
                    </span>
                  ))}
                </div>
              </div>

              <div className="cs-skills-group">
                <div className="cs-skills-label">Leadership & collaboration</div>
                <div className="cs-skill-tags">
                  {c.skills.leadership.map((s) => (
                    <span key={s} className="cs-tag">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

